import { useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';
import type { PostListItem, SetViewCountInput } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The post whose view count is being edited (null when closed). */
  post: PostListItem | null;
  isPending: boolean;
  /** Caller submits the chosen mode + value. Dialog stays open while
   *  pending; caller closes via onOpenChange on success. */
  onConfirm: (input: SetViewCountInput) => void;
}

const VIEW_COUNT_MAX = 100_000_000;

/**
 * Super-admin-only manual view-count editor. Two modes:
 *  - "ตั้งค่า" (set)  → view_count = value
 *  - "บวกเพิ่ม" (add) → view_count += value
 * Shows the current count and a live preview of the resulting total so
 * the admin sees exactly what will be written before confirming. Never
 * touches daily buckets, so trending is unaffected (mirrors the API).
 */
export function SetViewCountDialog({ open, onOpenChange, post, isPending, onConfirm }: Props) {
  const [mode, setMode] = useState<'set' | 'add'>('set');
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<string | null>(null);

  const current = post?.viewCount ?? 0;

  useEffect(() => {
    if (open) {
      // Default to "set" pre-filled with the current value so a small
      // tweak is a one-character edit rather than typing from scratch.
      setMode('set');
      setRaw(String(current));
      setError(null);
    }
  }, [open, current]);

  const value = Number(raw);
  const valid =
    raw.trim() !== '' && Number.isInteger(value) && value >= 0 && value <= VIEW_COUNT_MAX;
  const preview = valid ? Math.min(mode === 'add' ? current + value : value, VIEW_COUNT_MAX) : null;

  const handleConfirm = () => {
    if (!valid) {
      setError('กรุณากรอกจำนวนเต็มไม่ติดลบ (สูงสุด 100,000,000)');
      return;
    }
    onConfirm({ mode, value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-slate-400" />
            แก้ยอดวิว
          </DialogTitle>
          <DialogDescription>
            {post ? `"${post.title}"` : ''} — ยอดปัจจุบัน{' '}
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {current.toLocaleString()}
            </span>{' '}
            วิว. ไม่กระทบ trending (ไม่แตะยอดรายวัน)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => {
                setMode('set');
                setError(null);
              }}
              className={
                mode === 'set'
                  ? 'bg-brand-600 rounded-md px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              }
            >
              ตั้งค่า (=)
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('add');
                setError(null);
              }}
              className={
                mode === 'add'
                  ? 'bg-brand-600 rounded-md px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              }
            >
              บวกเพิ่ม (+)
            </button>
          </div>

          <Label htmlFor="view-count-value" className="text-xs">
            {mode === 'set' ? 'ยอดวิวใหม่' : 'จำนวนที่จะบวกเพิ่ม'}
          </Label>
          <Input
            id="view-count-value"
            type="number"
            min={0}
            max={VIEW_COUNT_MAX}
            step={1}
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirm();
            }}
          />

          {preview !== null && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ผลลัพธ์: <span className="font-medium">{current.toLocaleString()}</span>
              {mode === 'add' && (
                <>
                  {' + '}
                  <span className="font-medium">{value.toLocaleString()}</span>
                </>
              )}
              {' → '}
              <span className="text-brand-600 dark:text-brand-400 font-semibold">
                {preview.toLocaleString()}
              </span>{' '}
              วิว
            </p>
          )}
          {error && <p className="text-destructive text-xs">{error}</p>}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            ยกเลิก
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isPending || !valid}>
            {isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
